import { useEffect, useRef } from "react";
import {
  useLiveClinic,
  liveTelHref,
  liveMapsHref,
  liveOneLineAddress,
} from "@/lib/clinicLive";

type Variant = "compact" | "full";

interface ClinicNAPProps {
  variant?: Variant;
  className?: string;
}

/**
 * NAP (Name · Address · Phone · Hours) widget.
 *
 * Reads from the live clinic settings overlay so admin edits in
 * /admin/clinic-settings propagate instantly across header, footer,
 * and JSON-LD without a redeploy.
 */
const ClinicNAP = ({ variant = "full", className = "" }: ClinicNAPProps) => {
  const clinic = useLiveClinic();
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (import.meta.env.PROD) return;
    const el = rootRef.current;
    if (!el) return;

    const text = el.innerText.replace(/\s+/g, " ").trim();
    const expected = [
      clinic.name,
      clinic.address.street,
      clinic.address.postalCode,
      clinic.phone.display,
    ];
    const missing = expected.filter((v) => !text.includes(v));
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `[NAP] Inconsistency in <ClinicNAP variant="${variant}"/>: missing`,
        missing,
      );
    }
  }, [variant, clinic]);

  const telHref = liveTelHref();
  const mapsHref = liveMapsHref();

  if (variant === "compact") {
    return (
      <div
        ref={rootRef}
        data-nap-source="header"
        itemScope
        itemType="https://schema.org/MedicalBusiness"
        className={`flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-muted-foreground ${className}`}
      >
        <meta itemProp="name" content={clinic.name} />
        <span
          itemProp="address"
          itemScope
          itemType="https://schema.org/PostalAddress"
          className="hidden lg:inline"
        >
          <span itemProp="streetAddress">{clinic.address.street}</span>
          <span className="opacity-50 mx-2">·</span>
          <span itemProp="postalCode">{clinic.address.postalCode}</span>
        </span>
        <a
          href={telHref}
          itemProp="telephone"
          className="hover:text-accent transition-colors whitespace-nowrap"
        >
          {clinic.phone.display}
        </a>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-nap-source="footer"
      itemScope
      itemType="https://schema.org/MedicalBusiness"
      className={`space-y-5 ${className}`}
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Visit</p>
        <p className="font-display text-lg leading-snug" itemProp="name">
          {clinic.name}
        </p>
        <address
          itemProp="address"
          itemScope
          itemType="https://schema.org/PostalAddress"
          className="not-italic font-light opacity-90 leading-relaxed mt-1"
        >
          <span itemProp="streetAddress">{clinic.address.street}</span>
          <br />
          <span itemProp="addressLocality">{clinic.address.locality}</span>,{" "}
          <span itemProp="addressRegion">{clinic.address.region}</span>{" "}
          <span itemProp="postalCode">{clinic.address.postalCode}</span>
          <br />
          <span itemProp="addressCountry">{clinic.address.country}</span>
        </address>
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-3 text-[10px] tracking-[0.3em] uppercase hover:text-accent transition-colors opacity-70"
          aria-label={`Open ${liveOneLineAddress()} in Google Maps`}
        >
          Open in Maps →
        </a>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Call</p>
        <a
          href={telHref}
          itemProp="telephone"
          className="block font-light opacity-90 hover:text-accent transition-colors"
        >
          {clinic.phone.display}
        </a>
        {clinic.whatsapp.url && (
          <a
            href={clinic.whatsapp.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-1 font-light opacity-90 hover:text-accent transition-colors"
            aria-label={`Message ${clinic.name} on WhatsApp`}
          >
            <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 mr-2">WhatsApp</span>
            {clinic.whatsapp.display}
          </a>
        )}
        <p className="font-light opacity-70 text-sm mt-2">{clinic.bookingNote}</p>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.35em] mb-3 opacity-60">Hours</p>
        <ul className="space-y-1 text-sm font-light opacity-90">
          {clinic.hours.map((row) => (
            <li key={row.days} className="flex justify-between gap-6">
              <span>{row.days}</span>
              <span className="opacity-80">{row.time}</span>
            </li>
          ))}
        </ul>
        {clinic.hours
          .filter((r) => r.opens && r.closes)
          .map((r) => (
            <meta
              key={r.days}
              itemProp="openingHours"
              content={`${r.schema.join(",")} ${r.opens}-${r.closes}`}
            />
          ))}
      </div>
    </div>
  );
};

export default ClinicNAP;
